import {MuWidget} from "mu-widget/lib/MuWidget";
import {formatEnergy} from "../utils";
import {DayFoodPlan} from "../types";
import {DateTime} from "mu-js-utils/lib/DateTime";
import {formatDuration} from "../filters";

export class UiDayFoodPlan extends MuWidget
{
    private lastTotalEnergy = 0;
    updateEnergyView(totalEnergy: number|null = null) {
        if (totalEnergy === null) totalEnergy = this.lastTotalEnergy;
        else this.lastTotalEnergy = totalEnergy;
        const itemWidgets = this.muGetChildWidgets<UiDayFoodPlanItem>('items');
        const totalRel = itemWidgets.reduce(
            (sum, widget) => sum + widget.muFetchData().energyRel,
            0
        )
        itemWidgets.forEach(childWidget => childWidget.updateEnergyView(totalEnergy, totalRel));
    }

    beforeIndex() {
        this.muAppendContent(`
            <div class="day-food-plan">
                <div mu="items#items:@foreach">
                    <div mu=":UiDayFoodPlanItem@item"></div>
                </div>
                <input type="time" mu="endTime#endTime" class="day-food-plan__end-time" />
            </div>
            
        `);
    }

    muBindData(srcData: DayFoodPlan) {
        const items = [];
        for (let i = 0; i < srcData.energies.length; i++) {
            items.push({
                time: srcData.times[i],
                energyRel: srcData.energies[i],
                index: i,
            });
        }
        if (items.length < 0) items.push({
            time: '07:00',
            energyRel: 100,
            index: 0,
        });
        super.muBindData({
            endTime: DateTime.secondsToInput(srcData.times[srcData.times.length - 1], 'minute'),
        });
        this.indexAndRecalculate(items);
        super.muBindData({ items });
        this.updateEnergyView();
    }

    muFetchData(): DayFoodPlan {
        const items = this.muGetChildWidgets<UiDayFoodPlanItem>('items')
            .map(item => item.muFetchData());
        const endTime = DateTime.secondsFromInput(this.ui.endTime.value);
        return {
            times: [...items.map(item => item.time), endTime],
            energies: items.map(item => item.energyRel),
        };
    }

    splitItem(index: number) {
        const items = this.muGetChildWidgets('items')
            .map(item => item.muFetchData());
        if (index < 0 || index >= items.length) return;
        // console.log(JSON.parse(JSON.stringify(items)), 'splitItem', index);
        const endTime = DateTime.secondsFromInput(this.ui.endTime.value);

        const currentItem = items[index];
        const nextTime = items[index + 1] ? items[index + 1].time : endTime;

        const totalDuration = nextTime - currentItem.time;
        const newTime = nextTime - totalDuration / 2;

        const totalEnergy = currentItem.energyRel;

        const energyForCurrent = Math.round(totalEnergy / 2);
        const energyForNew = totalEnergy - energyForCurrent;

        currentItem.energyRel = energyForCurrent;

        const newItem = {
            energyRel: energyForNew,
            time: newTime
        };

        items.splice(index + 1, 0, newItem);

        // console.log(JSON.parse(JSON.stringify(items)));
        this.indexAndRecalculate(items);
        super.muBindData({ items });

        this.updateEnergyView();
    }

    deleteItem(index: number) {

        const items = this.muGetChildWidgets('items')
            .map(item => item.muFetchData());
        // console.log(JSON.parse(JSON.stringify(items)), 'deleteItem', index);
        let endTime = DateTime.secondsFromInput(this.ui.endTime.value);
        if (index < 0 || index >= items.length || items.length <= 1) {
            return;
        }

        const isFirst = index === 0;
        const isLast = index === items.length - 1;

        if (isFirst) {
            items.splice(index, 1);
        } else if (isLast) {
            const removedItem = items.pop();
            endTime = removedItem.time;
        } else {
            items[index - 1].energyRel += items[index].energyRel;
            items.splice(index, 1);
        }

        // console.log(JSON.parse(JSON.stringify(items)));
        this.indexAndRecalculate(items);
        super.muBindData({ items, endTime: DateTime.secondsToInput(endTime, 'minute') });

        this.updateEnergyView();
    }


    private indexAndRecalculate(items: any[]) {
        const max = Math.max(...items.map(item => item.energyRel));
        const mul = 100 / (max || 1);
        items.forEach((item: any, index) => {
            item.index = index;
            item.energyRel = item.energyRel * mul;
            item.nextTime = items[index + 1]?.time ?? DateTime.secondsFromInput(this.ui.endTime.value);
        });
    }
}

export class UiDayFoodPlanItem extends MuWidget
{
    // @ts-ignore
    muParent: UiDayFoodPlan;

    item: any;

    index: number = 0;
    beforeIndex() {
        this.muAppendContent(`
            <span class="day-food-plan-item__time">
                <input type="time" mu="time#time;timeMin:min;timeMax:max" style="width: 10rem" />
            </span>
            <span class="day-food-plan-item__add">
                <span class="icon--plus hover-click-pulse-15 cursor-pointer" mu="bSplit"></span>
            </span>
            <span class="day-food-plan-item__delete">
                <span class="icon--delete hover-click-pulse-15 cursor-pointer" mu="bDelete"></span>
            </span>
            <span class="day-food-plan-item__energy">
                <input type="range" min="0" max="100" mu="energyRel#energyRel" />
            </span>
            <span class="day-food-plan-item__info">
                <span class="day-food-plan-item__display-time" mu="#displayTime"></span> |
                <span class="day-food-plan-item__value" mu="#energyAbs"></span> |
                <span class="day-food-plan-item__value-percent" mu="#energyPercent"></span>
            </span>
        `);
        this.container.classList.add('day-food-plan-item');
    }

    muFetchData(): any {
        const data = super.muFetchData();
        return {
            energyRel: parseInt(data.energyRel),
            time: DateTime.secondsFromInput(data.time),
        };
    }

    muBindData(srcData: any) {
        this.index = srcData.index;
        this.item = srcData;
        super.muBindData({
            energyRel: srcData.energyRel,
            time: DateTime.secondsToInput(srcData.time, 'minute'),
        });
    }

    updateEnergyView(totalEnerg: number, totalRel: number) {

        const percent = parseInt(this.ui.energyRel.value) * 100 / (totalRel || 1);
        super.muBindData({
            energyPercent: (Math.round(percent * 10) / 10).toString()  + '%',
            energyAbs: formatEnergy(Math.round(totalEnerg * percent / 100)),
            displayTime: /*this.ui.time.value + ' - ' + DateTime.secondsToInput(this.item.nextTime, 'minute')
                + ' ' +*/ formatDuration(this.item.nextTime - DateTime.secondsFromInput(this.ui.time.value)),
        });
    }

    energyRel_input() {
        this.muParent.updateEnergyView();
    }

    bSplit_click() {
        this.muParent.splitItem(this.index);
    }

    bDelete_click() {
        this.muParent.deleteItem(this.index);
    }
}
